import { createSelector } from 'reselect';

import { AppState } from 'src/config/appState';

import { ProgramEnum, ProgramSharedStep, ProgramWeekEnum } from '../../models/programs';
import { GenericProgramStep } from '../../models/programsGeneric';
import { extractProgramId } from '../../surveys/selectors/surveysSelectors';
import { ProgramFeedback, WeekFeedback, WeeksAndProgramFeedback } from '../models/feedback';

export const extractWeekId = (_state: AppState, _programId: ProgramEnum, weekId: ProgramWeekEnum) =>
    weekId;

export const getFeedback = (state: AppState) => state.feedbacks;

export const getProgramsFeedback = (state: AppState) => getFeedback(state).programsFeedback;

export const getSingleProgramFeedback = createSelector(
    getProgramsFeedback,
    extractProgramId,
    (programsFeedback, programId): WeeksAndProgramFeedback | undefined =>
        programsFeedback && programsFeedback[programId],
);

export const getSingleProgramWeeksFeedbacks = createSelector(
    getSingleProgramFeedback,
    programFeedbacks => programFeedbacks && programFeedbacks.weeksFeedbacks,
);

export const getSingleProgramSingleFeedback = createSelector(
    getSingleProgramFeedback,
    (programFeedbacks): ProgramFeedback | undefined =>
        programFeedbacks && programFeedbacks.programFeedback,
);

export const isStepProgramFeedback = (stepId: GenericProgramStep) =>
    stepId === ProgramSharedStep.ProgramFeedbackStep;

export const getWeekFeedback = createSelector(
    getSingleProgramWeeksFeedbacks,
    extractWeekId,
    (feedback, weekId): WeekFeedback | undefined => feedback && feedback[weekId],
);

export const getPreviousWeekFeedback = createSelector(
    getSingleProgramWeeksFeedbacks,
    extractWeekId,
    (feedback, weekId) => {
        if (!feedback || !weekId || !Object.values(ProgramWeekEnum).includes(weekId)) {
            return undefined;
        }

        const currentWeekIndex = Object.keys(feedback).indexOf(weekId);

        return feedback[Object.keys(feedback)[currentWeekIndex > 0 ? currentWeekIndex - 1 : 0]];
    },
);

export const getFeedbackOrProgramFeedback = (
    state: AppState,
    programId: ProgramEnum,
    weekId: ProgramWeekEnum,
    stepId: GenericProgramStep,
) =>
    isStepProgramFeedback(stepId)
        ? getSingleProgramSingleFeedback(state, programId)
        : getPreviousWeekFeedback(state, programId, weekId);

export const validateFeedback = (feedback: WeekFeedback | ProgramFeedback | undefined) =>
    !!(feedback && feedback.rating && feedback.rating > 0);

export const isCurrentFeedbackStepValid = (
    state: AppState,
    programId: ProgramEnum,
    weekId: ProgramWeekEnum,
    stepId: GenericProgramStep,
) => {
    const feedback = isStepProgramFeedback(stepId)
        ? getSingleProgramSingleFeedback(state, programId)
        : getPreviousWeekFeedback(state, programId, weekId, stepId);

    return validateFeedback(feedback);
};
